import { Inbox, Loader2 } from "lucide-react";
import { SEVERITY_STYLE, STATUS_STYLE } from "@/lib/constants";
import { BRAND_THEME } from "@/lib/constants";
import { Skeleton } from "@/components/ui/skeleton";

export const StatusChip = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${STATUS_STYLE[status] || STATUS_STYLE.Closed}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
    {status || "—"}
  </span>
);

export const SeverityChip = ({ severity }) => (
  <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${SEVERITY_STYLE[severity] || SEVERITY_STYLE.Low}`}>
    {severity || "—"}
  </span>
);

export const BrandTag = ({ brand }) => {
  const t = BRAND_THEME[brand] || BRAND_THEME.All;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold whitespace-nowrap" style={{ background: t.bg, color: t.text }}>
      <span className="h-2 w-2 rounded-full" style={{ background: t.primary }} />
      {brand}
    </span>
  );
};

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 text-base text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const EmptyState = ({ title, text, action, icon: Icon = Inbox, testid = "empty-state" }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
    <div className="mb-4 rounded-full bg-slate-100 p-4"><Icon className="h-7 w-7 text-slate-400" /></div>
    <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
    {text && <p className="mt-1 max-w-md text-sm text-slate-500">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Card = ({ title, subtitle, right, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 ${className}`}>
    {(title || right) && (
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          {title && <h3 className="text-base font-semibold text-slate-800 sm:text-lg">{title}</h3>}
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

export const LoadingBlock = ({ rows = 4 }) => (
  <div className="space-y-3" data-testid="loading-skeleton">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
  </div>
);

export const Spinner = ({ className = "h-4 w-4" }) => <Loader2 className={`${className} animate-spin`} />;
